import { useLayoutEffect, useRef, useState } from 'react';
import OrgNodeCard from './OrgNodeCard';
import './orgChart.css';

/**
 * Top-down tree. Connectors are CSS pseudo elements on the ul/li structure, so
 * the markup nesting has to mirror the hierarchy exactly.
 *
 * A non-matching node is DIMMED, never removed: it may be the only path to a
 * node that does match, and dropping it would detach that subtree.
 */
function TreeNode({ node, collapsed, onToggle, matches, focusId, isAdmin }) {
  const hasKids = node.children.length > 0;
  const isCollapsed = collapsed.has(node.id);
  const dim = !matches(node);

  return (
    <li>
      <div
        className={focusId === node.id ? 'oc-focus' : undefined}
        style={{ position: 'relative', display: 'inline-block', opacity: dim ? 0.62 : 1 }}
      >
        <OrgNodeCard node={node} variant="tree" isAdmin={isAdmin} />
        {hasKids && (
          <button
            type="button"
            className="oc-toggle"
            onClick={() => onToggle(node.id)}
            aria-label={isCollapsed ? `Expand ${node.name}` : `Collapse ${node.name}`}
          >
            <span aria-hidden="true">{isCollapsed ? '+' : '-'}</span>
            <span>{node.children.length}</span>
          </button>
        )}
      </div>
      {hasKids && !isCollapsed && (
        <ul>
          {node.children.map((c) => (
            <TreeNode
              key={c.id} node={c} collapsed={collapsed} onToggle={onToggle}
              matches={matches} focusId={focusId} isAdmin={isAdmin}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * `transform: scale()` shrinks or grows the tree visually but leaves its
 * unscaled layout box in place, so `.oc-viewport` (overflow: auto) keeps
 * reserving the full, unscaled width and height. At zoom < 1 that shows up as
 * a large empty band around a small tree; at zoom > 1 the scrollable area
 * does not grow to match the now-larger visible content.
 *
 * The fix measures the tree's natural (unscaled) size and sizes an outer
 * wrapper to `natural * zoom`, so the viewport's scrollable box always
 * tracks the visible, scaled size instead of the unscaled one.
 * `transform-origin: top center` on `.oc-tree` itself (in orgChart.css) is
 * what keeps the tree horizontally centred inside that wrapper.
 */
export default function OrgTreeView({
  roots, collapsed, onToggle, matches, focusId, isAdmin, zoom = 1,
  centerSignal = 0, viewportRef: externalViewportRef, fullscreenOverlay = null,
  onMeasure,
}) {
  const innerRef = useRef(null);
  const [natural, setNatural] = useState(null);

  // Kept in a ref so the measuring effect below does not have to depend on
  // `onMeasure`: an inline arrow from the parent would be a new identity every
  // render, re-running the effect and re-creating the ResizeObserver each time.
  const onMeasureRef = useRef(onMeasure);
  onMeasureRef.current = onMeasure;

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const measure = () => {
      // offsetWidth/Height read the unscaled layout box: scale() does not
      // affect layout, only paint, so this stays the natural size regardless
      // of the current zoom.
      //
      // Returning the previous object when the size is unchanged keeps
      // ResizeObserver's sub-pixel churn from causing pointless re-renders. It
      // is not what fixed panning: that was a clipped wrapper plus a centre
      // transform origin, see the comment on .oc-tree-scale-wrap in the CSS.
      const width = el.offsetWidth;
      const height = el.offsetHeight;
      setNatural((prev) => (
        prev && prev.width === width && prev.height === height ? prev : { width, height }
      ));
      // Lifted to the page so the PDF export can decide whether the tree fits a
      // landscape page or has to fall back to the list. The page cannot measure
      // this itself: the tree's natural size is only knowable from the element,
      // and it is this component that owns it.
      if (onMeasureRef.current) onMeasureRef.current(width);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [roots, collapsed]);

  // The collapse toggle sits at bottom: -12px on its card, so it hangs below the
  // tree's own box. Sizing the wrapper to exactly the scaled height cut the
  // toggle off the bottom row, so add back the overhang (scaled with the tree).
  const TOGGLE_OVERHANG = 16;
  // minWidth 100% keeps a tree NARROWER than the viewport spanning it, so the
  // margin auto below still centres it. Without this the wrapper shrinks to the
  // scaled tree and a small org sits hard against the left edge. The explicit
  // width is what makes the viewport scrollable for a tree wider than it.
  const wrapperStyle = natural
    ? {
      width: natural.width * zoom,
      minWidth: '100%',
      height: natural.height * zoom + TOGGLE_OVERHANG * zoom,
    }
    : undefined;

  // A tree wider than the viewport otherwise opens scrolled hard left, which
  // clips the root card. Centre the scroll on the content once, on FIRST layout,
  // so the top of the hierarchy is what the reader lands on, and thereafter only
  // when the toolbar's Center button bumps `centerSignal`.
  //
  // Deliberately NOT keyed on the tree's width any more. Expanding a node widens
  // the tree, which changed the width, which re-ran this effect and hard-centred
  // the whole tree: the reader clicked a node on one side and the viewport jumped
  // away from it, which reads as the scroll flying off to the left. Expanding
  // should leave the reader looking at what they expanded, so growth alone must
  // never move the scroll. Zoom is the same argument: it happens around wherever
  // the reader is looking, and the Center button is right there when they do want
  // recentring.
  const viewportRef = useRef(null);
  // Guards the initial centring so it happens exactly once per mount, on the
  // first SETTLED layout.
  //
  // "Settled" is the important word. The page mounts the tree fully expanded and
  // only then collapses everything below the first level (a useEffect in
  // OrgChartPage, so it runs after paint). Centring on the first measured width
  // therefore centred on the fully expanded tree, which is several times wider
  // than the collapsed one; the collapse then shrank the content under an
  // already-large scrollLeft and left the reader scrolled far to the right.
  //
  // `treeReady` is what waits for that: on a tree that has collapsible nodes,
  // hold off until the default collapse has actually been applied. A tree with
  // nothing to collapse is already settled on its first layout.
  const hasCollapsibleNodes = roots.some((r) => r.children.length > 0);
  const treeReady = !hasCollapsibleNodes || collapsed.size > 0;
  const didInitialCenterRef = useRef(false);
  useLayoutEffect(() => {
    const vp = viewportRef.current;
    const el = innerRef.current;
    if (!vp || !el || !treeReady) return;
    if (didInitialCenterRef.current) return;
    // Measured HERE rather than read from `natural`. The measuring effect above
    // and this one both run on a `collapsed` change, and `setNatural` is async,
    // so `natural` still holds the PREVIOUS (fully expanded) width on the render
    // where the collapse first lands. Centring on that stale value is exactly the
    // far-right scroll this effect exists to avoid, so take the width straight
    // from the element, which is already laid out by the time a layout effect
    // runs.
    const width = el.offsetWidth * zoom;
    if (!width) return;
    didInitialCenterRef.current = true;
    const overflow = width - vp.clientWidth;
    vp.scrollLeft = overflow > 0 ? overflow / 2 : 0;
  }, [treeReady, zoom]);

  // The Center button, as its own effect so it is the ONLY thing that recentres
  // after the initial layout. Skips the mount run, which the effect above owns.
  const lastCenterSignalRef = useRef(centerSignal);
  useLayoutEffect(() => {
    if (lastCenterSignalRef.current === centerSignal) return;
    lastCenterSignalRef.current = centerSignal;
    const vp = viewportRef.current;
    const el = innerRef.current;
    if (!vp || !el) return;
    // Measured from the element for the same reason as the initial centring
    // above: `natural` can lag a layout change by one render.
    const width = el.offsetWidth * zoom;
    if (!width) return;
    const overflow = width - vp.clientWidth;
    vp.scrollLeft = overflow > 0 ? overflow / 2 : 0;
  }, [centerSignal, zoom]);

  // The page owns a ref to this same element so the Fullscreen control can
  // call requestFullscreen() on the actual chart viewport. Setting both refs
  // from one callback keeps a single DOM node as the source of truth instead
  // of syncing two refs against each other after the fact.
  const setViewportRefs = (el) => {
    viewportRef.current = el;
    if (externalViewportRef) externalViewportRef.current = el;
  };

  return (
    <div className="oc-viewport" ref={setViewportRefs}>
      <div className="oc-tree-scale-wrap" style={wrapperStyle}>
        <div className="oc-tree" ref={innerRef} style={{ transform: `scale(${zoom})` }}>
          <ul>
            {roots.map((r) => (
              <TreeNode
                key={r.id} node={r} collapsed={collapsed} onToggle={onToggle}
                matches={matches} focusId={focusId} isAdmin={isAdmin}
              />
            ))}
          </ul>
        </div>
      </div>
      {fullscreenOverlay}
    </div>
  );
}
